<template>
  <div class="fcc-container" v-if="isVisible">
    <div class="fcc-header">
      <h3>False Color Composite</h3>
      <Button 
        icon="pi pi-times" 
        @click="handleCancel"
        severity="secondary"
        outlined
        size="small"
        class="cancel-btn"
        aria-label="Cancel FCC Mode"
      />
    </div>
    
    <div class="fcc-info">
      <small class="text-muted">
        Click files in the list below to assign them to RGB channels
      </small>
    </div>

    <div class="fcc-bands">
      <div class="band-slot" :class="{ 'has-file': redBand }">
        <div class="band-header">
          <span class="band-label red">Red (R)</span>
          <Button 
            v-if="redBand" 
            icon="pi pi-times" 
            @click="clearBand('red')"
            severity="danger"
            text
            size="small"
            class="clear-band-btn"
          />
        </div>
        <div 
          class="drop-zone red" 
          @drop="handleDrop($event, 'red')"
          @dragover="handleDragOver"
          @dragenter="handleDragEnter"
        >
          <div v-if="redBand" class="band-file">
            <span class="file-name">{{ redBand.name }}</span>
            <small class="file-type">{{ getFileDisplayName(redBand) }}</small>
          </div>
          <div v-else class="empty-slot">
            <i class="pi pi-plus"></i>
            <span>Drop file or click to assign</span>
          </div>
        </div>
      </div>

      <div class="band-slot" :class="{ 'has-file': greenBand }">
        <div class="band-header">
          <span class="band-label green">Green (G)</span>
          <Button 
            v-if="greenBand" 
            icon="pi pi-times" 
            @click="clearBand('green')"
            severity="danger"
            text
            size="small"
            class="clear-band-btn"
          />
        </div>
        <div 
          class="drop-zone green" 
          @drop="handleDrop($event, 'green')"
          @dragover="handleDragOver"
          @dragenter="handleDragEnter"
        >
          <div v-if="greenBand" class="band-file">
            <span class="file-name">{{ greenBand.name }}</span>
            <small class="file-type">{{ getFileDisplayName(greenBand) }}</small>
          </div>
          <div v-else class="empty-slot">
            <i class="pi pi-plus"></i>
            <span>Drop file or click to assign</span>
          </div>
        </div>
      </div>

      <div class="band-slot" :class="{ 'has-file': blueBand }">
        <div class="band-header">
          <span class="band-label blue">Blue (B)</span>
          <Button 
            v-if="blueBand" 
            icon="pi pi-times" 
            @click="clearBand('blue')"
            severity="danger"
            text
            size="small"
            class="clear-band-btn"
          />
        </div>
        <div 
          class="drop-zone blue" 
          @drop="handleDrop($event, 'blue')"
          @dragover="handleDragOver"
          @dragenter="handleDragEnter"
        >
          <div v-if="blueBand" class="band-file">
            <span class="file-name">{{ blueBand.name }}</span>
            <small class="file-type">{{ getFileDisplayName(blueBand) }}</small>
          </div>
          <div v-else class="empty-slot">
            <i class="pi pi-plus"></i>
            <span>Drop file or click to assign</span>
          </div>
        </div>
      </div>
    </div>

    <div class="fcc-actions">
      <Button 
        label="Create Composite" 
        @click="createComposite"
        :disabled="!canCreateComposite"
        severity="success"
        class="create-btn"
      />
      <Button 
        label="Clear All" 
        @click="clearAll"
        severity="secondary"
        outlined
        class="clear-all-btn"
      />
    </div>

    <div v-if="isCreating" class="fcc-loading">
      <ProgressSpinner size="small" />
      <span>Creating false color composite...</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { Button, ProgressSpinner } from 'primevue'
import WebGLTileLayer from 'ol/layer/WebGLTile'
import GeoTIFFSource from 'ol/source/GeoTIFF'
import type { File } from '@/composables/useFiles'

// Define props using modern syntax
const { siteName, isVisible } = defineProps<{
  siteName: string
  isVisible: boolean
}>()

const emit = defineEmits<{
  'composite-created': [layer: WebGLTileLayer]
  'composite-cleared': []
  'cancel': []
}>()

// Reactive state
const redBand = ref<File | null>(null)
const greenBand = ref<File | null>(null)
const blueBand = ref<File | null>(null)
const isCreating = ref(false)

// Computed properties
const canCreateComposite = computed(() => {
  return redBand.value && greenBand.value && blueBand.value && !isCreating.value
})

// Helper functions
const getFileDisplayName = (file: File) => {
  if (file.type && file.type !== 'unknown') {
    let displayName = file.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    
    if (file.date_string) {
      const formattedDate = file.date_string.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')
      displayName += ` - ${formattedDate}`
    }
    
    return displayName
  }
  
  return file.name
}

const isTiffFile = (file: File) => {
  const extension = file.name.split('.').pop()?.toLowerCase()
  return extension === 'tif' || extension === 'tiff'
}

const getMinMaxValues = (file: File) => {
  const filename = file.name.toLowerCase()
  
  if (filename.includes('true_color') || filename.includes('truecolor')) {
    return { min: 0, max: 255 }
  } else if (filename.includes('coherence')) {
    return { min: 0, max: 1 }
  } else if (filename.includes('vv') || filename.includes('vh')) {
    return { min: 3.5, max: 5.5 }
  } else if (filename.includes('phase') || filename.includes('displacement')) {
    return { min: -3.14, max: 3.14 }
  } else if (filename.includes('vv_mean')) {
    return { min: 60, max: 120 }
  }
  
  // Default values
  return { min: 0, max: 1 }
}

// Public methods (exposed via defineExpose)
const addFile = (file: File): boolean => {
  if (!isTiffFile(file)) {
    console.warn('Only TIFF files can be added to false color composite')
    return false
  }

  // Find the first empty band
  if (!redBand.value) {
    redBand.value = file
    return true
  } else if (!greenBand.value) {
    greenBand.value = file
    return true
  } else if (!blueBand.value) {
    blueBand.value = file
    return true
  }
  
  console.warn('All RGB bands are full. Clear a band first.')
  return false
}

const clearBand = (band: 'red' | 'green' | 'blue') => {
  switch (band) {
    case 'red':
      redBand.value = null
      break
    case 'green':
      greenBand.value = null
      break
    case 'blue':
      blueBand.value = null
      break
  }
}

const clearAll = () => {
  redBand.value = null
  greenBand.value = null
  blueBand.value = null
  emit('composite-cleared')
}

// Drag and drop handlers
const handleDragOver = (event: DragEvent) => {
  event.preventDefault()
}

const handleDragEnter = (event: DragEvent) => {
  event.preventDefault()
}

const handleDrop = (event: DragEvent, targetBand: 'red' | 'green' | 'blue') => {
  event.preventDefault()
  
  const fileName = event.dataTransfer?.getData('text/plain')
  if (!fileName) return
  
  // This would need to be implemented if drag & drop from FileList is desired
  console.log(`Dropped ${fileName} on ${targetBand} band`)
}

// Main composite creation function
const createComposite = async () => {
  if (!canCreateComposite.value) return
  
  isCreating.value = true
  
  try {
    // Fetch the three files as blobs
    const [redBlob, greenBlob, blueBlob] = await Promise.all([
      fetchFileBlob(redBand.value!),
      fetchFileBlob(greenBand.value!),
      fetchFileBlob(blueBand.value!)
    ])

    // Get min/max values for each band
    const redMinMax = getMinMaxValues(redBand.value!)
    const greenMinMax = getMinMaxValues(greenBand.value!)
    const blueMinMax = getMinMaxValues(blueBand.value!)

    // Create the composite layer
    const compositeSource = new GeoTIFFSource({
      sources: [
        {
          blob: redBlob,
          min: redMinMax.min,
          max: redMinMax.max,
          nodata: -999
        },
        {
          blob: greenBlob,
          min: greenMinMax.min,
          max: greenMinMax.max,
          nodata: -999
        },
        {
          blob: blueBlob,
          min: blueMinMax.min,
          max: blueMinMax.max,
          nodata: -999
        }
      ],
      projection: 'EPSG:4326'
    })

    const compositeLayer = new WebGLTileLayer({
      source: compositeSource,
      opacity: 1,
      visible: true
    })

    // Add metadata to track this as a false color composite
    compositeLayer.set('layerType', 'false-color-composite')
    compositeLayer.set('bands', {
      red: redBand.value!.name,
      green: greenBand.value!.name,
      blue: blueBand.value!.name
    })

    emit('composite-created', compositeLayer)
    
    console.log('False color composite created successfully')
  } catch (error) {
    console.error('Error creating false color composite:', error)
  } finally {
    isCreating.value = false
  }
}

// Helper function to fetch file as blob
const fetchFileBlob = async (file: File): Promise<Blob> => {
  const fileUrl = `/api/media/protected_image/${siteName}/${file.name}`
  
  const response = await fetch(fileUrl)
  if (!response.ok) {
    throw new Error(`Failed to fetch ${file.name}: ${response.statusText}`)
  }
  
  return await response.blob()
}

const handleCancel = () => {
  emit('cancel')
}

// Expose methods for parent component
defineExpose({
  addFile,
  clearAll
})
</script>

<style scoped>
.fcc-container {
  background: var(--surface-ground);
  border: 1px solid var(--surface-border);
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.fcc-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.fcc-header h3 {
  margin: 0;
  color: var(--text-color);
  font-size: 1.1rem;
}

.cancel-btn {
  margin-left: auto;
}

.fcc-info {
  margin-bottom: 1rem;
}

.text-muted {
  color: var(--text-color-secondary);
  font-style: italic;
}

.fcc-bands {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.band-slot {
  border: 1px solid var(--surface-border);
  border-radius: 4px;
  overflow: hidden;
}

.band-slot.has-file {
  border-color: var(--primary-color);
}

.band-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: var(--surface-100);
}

.band-label {
  font-weight: 600;
  font-size: 0.875rem;
}

.band-label.red {
  color: #dc2626;
}

.band-label.green {
  color: #16a34a;
}

.band-label.blue {
  color: #2563eb;
}

.clear-band-btn {
  padding: 0.25rem;
}

.drop-zone {
  padding: 0.75rem;
  min-height: 60px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s;
}

.drop-zone:hover {
  background: var(--surface-50);
}

.drop-zone.red:hover {
  background: rgba(220, 38, 38, 0.05);
}

.drop-zone.green:hover {
  background: rgba(22, 163, 74, 0.05);
}

.drop-zone.blue:hover {
  background: rgba(37, 99, 235, 0.05);
}

.band-file {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
}

.file-name {
  font-weight: 500;
  color: var(--text-color);
  font-size: 0.875rem;
}

.file-type {
  color: var(--text-color-secondary);
  font-size: 0.75rem;
}

.empty-slot {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-color-secondary);
  width: 100%;
  justify-content: center;
}

.empty-slot i {
  font-size: 1rem;
}

.fcc-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.create-btn {
  flex-shrink: 0;
}

.clear-all-btn {
  flex-shrink: 0;
}

.fcc-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: var(--surface-50);
  border-radius: 4px;
  margin-top: 0.75rem;
  color: var(--text-color-secondary);
  font-size: 0.875rem;
}
</style>
